import CopyToClipboard from '@bumble/app/components/CopyToClipboard';
import { Loading } from '@bumble/app/components/Loading';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { Box, Button, Card, CardContent, Divider, Grid, TextField, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CardRows } from '../components/CardRows';

export const Operations = () => {
  const api = useApi();

  const [webhook, setWebhook] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [key, setKey] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const { isLoading, data: opsData } = useQuery(
    ['getAccountOps'],
    api.getAccountOpsApiV1GetAccountOpsGet.bind(null, {}),
    {
      enabled: true,
      cacheTime: 0,
      staleTime: 0,
      refetchOnMount: true,
      initialData: undefined,
    }
  );

  useEffect(() => {
    setWebhook(opsData?.data[0]?.webhook_url || '');
    setEmail(opsData?.data[0]?.email || '');
    setKey(opsData?.data[0]?.signature_key || '');
  }, [opsData]);

  const setAccountOps = useMutation(api.setAccountOpsApiV1SetAccountOpsPost, {
    onSuccess(data) {
      toast.success('Account Operations updated.');
      setKey(data?.data?.signature_key);
      setLoading(false);
    },
    onError: apiError,
  });

  const handleAccountOpsSubmit = () => {
    setLoading(true);
    setAccountOps.mutate({
      webhook_url: webhook,
      email: email,
    });
  };

  const isDisabled = () => loading;

  const changeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setLoading(false);
  }

  const changeURL = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWebhook(e.target.value);
    setLoading(false);
  }

  if (isLoading) {
    return <Loading />;
  }
  return (
    <PageContent>
      <PageHeader pageName="Operations" />
      <Grid container spacing={5}>
        <Grid item xs={12} md={8}>
          <Card variant="outlined" elevation={0}>
            <CardContent>
              <Typography variant="subtitle1">Event Notifications</Typography>
              <Typography variant="body2" color="textSecondary">
                Configure email and webhook URL to receive event notifications.
              </Typography>
            </CardContent>
            <CardRows
              label="Emails"
              value={
                <TextField
                  fullWidth
                  size="small"
                  id="email"
                  label="Enter your email"
                  placeholder="email@domain.com"
                  variant="outlined"
                  value={email}
                  onChange={changeEmail}
                />
              }
            />
            <CardRows
              label="Webhook URL"
              value={
                <TextField
                  fullWidth
                  size="small"
                  id="webhook"
                  label="Enter the webhook URL"
                  placeholder="https://webhook.site/"
                  variant="outlined"
                  value={webhook}
                  onChange={changeURL}
                />
              }
            />
            <Box p={5}>
              <Button
                variant="contained"
                type="submit"
                disabled={isDisabled()}
                onClick={handleAccountOpsSubmit}
                fullWidth
              >
                Update Notifications
              </Button>
            </Box>
            <Divider />
            <CardRows
              label="Webhook Signature Key"
              value={
                <Box sx={{ position: 'relative', width: '100%' }}>
                  <TextField
                    fullWidth
                    label="Signature Key"
                    variant="outlined"
                    value={key}
                    multiline
                    minRows={2}
                    maxRows={2}
                    disabled
                  />
                  <Box sx={{ position: 'absolute', top: 5, right: 5, zIndex: 999 }}>
                    <CopyToClipboard content={key} />
                  </Box>
                </Box>
              }
            />
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" elevation={0}>
            <CardContent>
              <Typography variant="subtitle1">What are Event Notifications?</Typography>
              <Typography variant="body2" color="textSecondary">
                Event notifications allow you to receive real-time updates about critical system events
                via emails or webhooks. Emails notify you directly, while webhooks can trigger automated
                processes in your applications. (You can enter multiple emails separated by comma)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PageContent>
  );
};
